import type { IErrorValue } from "./types";

import { formatApiError } from "./errors";

const statusToDefaultMessage = {
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
};

export default async function customFetch<TData>(
  input: string | URL | Request,
  {
    name,
    getResultError = formatApiError,
    getResultData = (result, transformType) => {
      switch (transformType) {
        case "json":
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
          return result.data;
        case "blob":
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return result;
      }
    },
    transformType = "json",
    ...init
  }: RequestInit & {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getResultError?: (result: any) => string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getResultData?: (result: any, transformType: "json" | "blob") => TData;
    transformType?: "json" | "blob";
  },
) {
  let res: Response;
  try {
    res = await fetch(input, init);

    if (res.status in statusToDefaultMessage) {
      const result = (await res.json().catch(() => undefined)) as {
        error: { message: string };
      };

      throw new Error(
        getResultError(result) ??
          statusToDefaultMessage[
            res.status as keyof typeof statusToDefaultMessage
          ],
      );
    }

    const data = (
      transformType === "blob" ? await res.blob() : await res.json()
    ) as {
      error?: { message: string };
      data?: TData;
    };

    if (data.error) {
      throw new Error(getResultError(data) ?? `Error load data`);
    }

    if (!res.ok) {
      throw new Error(`Something Went Wrong`);
    }

    return getResultData(data, transformType);
  } catch (error) {
    const message = `Error in (${name}), ${
      error instanceof Error ? error.message : String(error)
    }`;

    console.error(
      `name: ${name}`,
      "\n",
      `Method: ${init.method}`,
      "\n",
      `URL: ${String(input)}`,
      "\n",
      `Error: ${message}`,
    );

    return { error: { message } } as IErrorValue;
  }
}
